import { FC } from 'react';

import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  MailIcon,
} from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
  GiftCouponByCode,
  GiftCouponRedeemStatus,
  NotificationType,
} from '@shared/api/giftCoupon/giftCoupon.interface';

import CouponProductCard from './CouponProductCard';
import {
  formatKoreanDate,
  getNextCouponSendSlot,
  RedeemStatusMessage,
} from './giftCouponRedeem.utils';
import SendScheduleCard from './SendScheduleCard';

interface CouponStatusViewProps {
  coupon: GiftCouponByCode;
  statusMessage: RedeemStatusMessage;
  imageUrl: string;
  itemName: string;
  couponCode: string;
  brandName: string | null;
}

const CouponStatusView: FC<CouponStatusViewProps> = ({
  coupon,
  statusMessage,
  imageUrl,
  itemName,
  couponCode,
  brandName,
}) => {
  const selectedUnit =
    coupon.selectedChoiceUnit && typeof coupon.selectedChoiceUnit === 'object'
      ? coupon.selectedChoiceUnit
      : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white px-4 py-8">
      <div className="mx-auto max-w-md">
        {/* 상태 메시지 */}
        <div
          className={`rounded-xl p-6 text-center ${
            statusMessage.type === 'success'
              ? 'border border-stone-200 bg-stone-50'
              : statusMessage.type === 'info'
              ? 'border border-stone-200 bg-stone-50'
              : 'border border-red-100 bg-red-50'
          }`}
        >
          {statusMessage.type === 'success' ? (
            <CheckCircleSolidIcon className="mx-auto h-10 w-10 text-stone-600" />
          ) : statusMessage.type === 'info' ? (
            <CheckCircleIcon className="mx-auto h-10 w-10 text-stone-500" />
          ) : (
            <ExclamationCircleIcon className="mx-auto h-10 w-10 text-red-400" />
          )}
          <h2 className="mt-3 text-lg font-medium text-stone-800">
            {statusMessage.title}
          </h2>
          {statusMessage.message && (
            <p className="mt-1 whitespace-pre-line text-sm text-stone-500">
              {statusMessage.message}
            </p>
          )}
        </div>

        {/* Delivered: 알림톡/문자 안내 */}
        {coupon.redeemStatus === GiftCouponRedeemStatus.Delivered &&
          (() => {
            const channel = coupon.gifticon?.deliveryChannel;
            const config =
              channel && channel in DeliveryChannel
                ? DELIVERY_CHANNEL_CONFIG[channel as DeliveryChannel]
                : null;
            const isAlimTalk =
              !config || config.notificationType === NotificationType.AlimTalk;
            return (
              <div className="mt-4 overflow-hidden rounded-xl border border-stone-200 bg-white text-center shadow-sm">
                <div className="px-5 py-4">
                  {isAlimTalk ? (
                    <>
                      <p className="text-base font-bold text-stone-800">
                        알림톡(카카오톡)을 확인해 주세요
                      </p>
                      <p className="mt-1.5 text-xs text-stone-400">
                        카카오톡으로 발송된 알림톡을 확인해 주세요. (카카오톡을 사용하지
                        않으시는 경우 문자 발송을 문의 메일로 요청해주세요.)
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-stone-700">
                        받으신 문자 메시지를 확인해 주세요
                      </p>
                      {config && (
                        <div className="mt-3 inline-flex items-center gap-2.5 rounded-lg border border-stone-200 bg-stone-50 px-5 py-2.5">
                          <span className="text-xs font-medium text-stone-400">
                            발신번호
                          </span>
                          <span className="text-base font-bold text-stone-800">
                            {config.label}
                          </span>
                        </div>
                      )}
                      <p className="mt-2.5 break-keep text-xs text-stone-400">
                        &quot;{config?.label}&quot; 번호로 발송된 문자 메시지를 확인해
                        주세요.
                      </p>
                    </>
                  )}
                </div>
                {coupon.gifticon?.deliveredAt && (
                  <div className="border-t border-stone-100 bg-stone-50 px-5 py-2.5">
                    <p className="text-sm font-semibold text-stone-700">
                      {formatKoreanDate(new Date(coupon.gifticon.deliveredAt))} 발송
                    </p>
                  </div>
                )}
              </div>
            );
          })()}

        {/* Requested: 발송 예정 안내 */}
        {coupon.redeemStatus === GiftCouponRedeemStatus.Requested &&
          coupon.recipient?.requestedAt &&
          (() => {
            const reqAt = new Date(coupon.recipient.requestedAt);
            const sendAt = getNextCouponSendSlot(reqAt);
            return <SendScheduleCard requestedAt={reqAt} expectedSendAt={sendAt} />;
          })()}

        {/* 상품 정보 */}
        <CouponProductCard
          imageUrl={imageUrl}
          itemName={itemName}
          couponCode={couponCode}
          brandName={brandName}
          selectedUnit={selectedUnit}
        />

        {!statusMessage.hideContact && (
          <div className="mt-6 text-center">
            <a
              href="mailto:smartnanumi@kakao.com"
              className="inline-flex items-center gap-1 text-xs text-stone-300 transition hover:text-stone-400"
            >
              <MailIcon className="h-3 w-3" />
              <span>문의 smartnanumi@kakao.com</span>
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default CouponStatusView;
